import type { Table, TableArea } from '../types'
import { appendAuditLog } from './auditLog'
import { useFirestore } from './firebase'
import * as firestoreTableAreas from './firestore/tableAreas'
import { sortTableAreasByIndex } from './menuSortOrder'

const STORAGE_KEY = 'wortelogy-table-areas'

export const UNASSIGNED_TABLE_AREA_ID = '__unassigned__'
export const UNASSIGNED_TABLE_AREA_NAME = 'Unassigned'
export const DEFAULT_TABLE_AREA_COLOR = '#6366f1'
export const UNASSIGNED_TABLE_AREA_COLOR = '#94a3b8'

const unassignedTableArea: TableArea = {
  id: UNASSIGNED_TABLE_AREA_ID,
  name: UNASSIGNED_TABLE_AREA_NAME,
  color: UNASSIGNED_TABLE_AREA_COLOR,
  sortIndex: 0,
}

/** Distinct colors cycled when staff adds a new area (avoid identical purple every time). */
export const TABLE_AREA_COLOR_PALETTE: readonly string[] = [
  '#6366f1', // indigo — keep first as classic default tone
  '#ea580c', // orange
  '#059669', // emerald
  '#db2777', // pink
  '#0d9488', // teal
  '#7c3aed', // violet
  '#ca8a04', // yellow-600
  '#2563eb', // blue
  '#b91c1c', // red
]

export function pickNextTableAreaColor(existingAreaCount: number): string {
  return TABLE_AREA_COLOR_PALETTE[existingAreaCount % TABLE_AREA_COLOR_PALETTE.length]!
}

function readAreasLocalUnsorted(): TableArea[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    let list: TableArea[] = raw ? (JSON.parse(raw) as TableArea[]) : []
    const hasUnassigned = list.some((a) => a.id === UNASSIGNED_TABLE_AREA_ID)
    if (!hasUnassigned) {
      list = [unassignedTableArea, ...list]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
    }
    return list
  } catch {
    return [unassignedTableArea]
  }
}

function getTableAreasLocal(): TableArea[] {
  return sortTableAreasByIndex(readAreasLocalUnsorted())
}

function saveTableAreasLocal(areas: TableArea[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(areas))
}

function generateTableAreaId(): string {
  return `TA-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function getTableAreas(): TableArea[] {
  if (useFirestore()) {
    return firestoreTableAreas.getTableAreas()
  }
  return getTableAreasLocal()
}

export function saveTableAreas(areas: TableArea[]): void {
  if (useFirestore()) {
    firestoreTableAreas.saveTableAreas(areas)
    return
  }
  const withIdx = areas.map((a, i) => ({ ...a, sortIndex: i }))
  saveTableAreasLocal(withIdx)
}

export function addTableArea(area: Omit<TableArea, 'id'>): TableArea {
  const full = useFirestore()
    ? firestoreTableAreas.addTableArea(area)
    : (() => {
        const id = generateTableAreaId()
        const list = readAreasLocalUnsorted()
        const maxIdx = list.reduce((m, a) => Math.max(m, a.sortIndex ?? -1), -1)
        const created: TableArea = {
          ...area,
          id,
          color: area.color?.trim() || DEFAULT_TABLE_AREA_COLOR,
          sortIndex: maxIdx + 1,
        }
        saveTableAreas([...list, created])
        return created
      })()
  appendAuditLog({
    domain: 'floor',
    action: 'area.created',
    message: `Created area ${full.name}`,
    entityType: 'tableArea',
    entityId: full.id,
    meta: { name: full.name },
  })
  return full
}

export function updateTableArea(id: string, patch: Partial<TableArea>): TableArea[] {
  if (id === UNASSIGNED_TABLE_AREA_ID) return getTableAreas()
  const before = getTableAreas().find((a) => a.id === id)
  const updated = useFirestore()
    ? firestoreTableAreas.updateTableArea(id, patch)
    : (() => {
        const list = readAreasLocalUnsorted()
        const next = list.map((a) =>
          a.id === id ? { ...a, ...patch, color: patch.color?.trim() || a.color } : a,
        )
        saveTableAreas(next)
        return getTableAreasLocal()
      })()
  const after = updated.find((a) => a.id === id)
  const keys = Object.keys(patch).join(', ')
  appendAuditLog({
    domain: 'floor',
    action: 'area.updated',
    message: `Updated area ${after?.name ?? before?.name ?? id} (${keys})`,
    entityType: 'tableArea',
    entityId: id,
    meta: { name: after?.name ?? before?.name ?? '', keys },
  })
  return updated
}

export function deleteTableArea(id: string): TableArea[] {
  if (id === UNASSIGNED_TABLE_AREA_ID) return getTableAreas()
  const before = getTableAreas().find((a) => a.id === id)
  const updated = useFirestore()
    ? firestoreTableAreas.deleteTableArea(id)
    : (() => {
        const filtered = readAreasLocalUnsorted().filter((a) => a.id !== id)
        saveTableAreas(filtered)
        return getTableAreasLocal()
      })()
  appendAuditLog({
    domain: 'floor',
    action: 'area.deleted',
    message: `Deleted area ${before?.name ?? id}`,
    entityType: 'tableArea',
    entityId: id,
    meta: { name: before?.name ?? '' },
  })
  return updated
}

export function reorderTableAreas(orderedIds: string[]): void {
  if (useFirestore()) {
    firestoreTableAreas.reorderTableAreas(orderedIds)
    return
  }
  const areas = getTableAreasLocal()
  const byId = new Map(areas.map((a) => [a.id, a]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((a): a is TableArea => a != null)
  saveTableAreas(newOrder)
}

/** Tables assigned to a deleted area should clear `areaId` (call from UI after delete). */
export function migrateTablesOffDeletedArea(tables: Table[], deletedAreaId: string): Table[] {
  return tables.map((t) =>
    t.areaId === deletedAreaId ? { ...t, areaId: undefined, sortIndex: t.sortIndex } : t,
  )
}
